import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconArrowLeft, IconArrowRight, IconPause, IconPlay } from '@runner/ui';
import { PracticeWorld } from '../data/demo/PracticeWorld';
import { WorldCanvas } from '../render/WorldCanvas';
import { usePrefs, SkinPicker } from '../app/prefs';
import '../styles/practice.css';

export function PracticeGame({ children, compact = false }: { children?: ReactNode; compact?: boolean }) {
  const [world, setWorld] = useState(() => new PracticeWorld());
  const [view, setView] = useState(() => world.getView());
  const [direction, setDirection] = useState<-1 | 0 | 1>(0);
  const seed = useRef(20261015);
  const { paused, setPaused } = usePrefs();
  const finished = view.phase !== 'running';
  useEffect(() => {
    if (finished) return;
    let last = performance.now();
    const timer = setInterval(() => {
      const now = performance.now();
      if (!paused && !document.hidden) {
        world.steer(direction);
        world.advance(Math.min(now - last, 250));
        setView(world.getView());
      }
      last = now;
    }, 100);
    return () => clearInterval(timer);
  }, [world, direction, paused, finished]);
  const steer = (value: -1 | 0 | 1) => {
    if (paused || finished) return;
    world.steer(value);
    setDirection(value);
    setView(world.getView());
  };
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || document.querySelector('[role="dialog"]')) return;
      if ((event.target as HTMLElement)?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const k = event.key.toLowerCase();
      const value = k === 'a' || k === 'arrowleft' ? -1 : k === 'd' || k === 'arrowright' ? 1 : k === 's' || k === 'arrowdown' ? 0 : null;
      if (value === null) return;
      event.preventDefault();
      steer(value);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [world, paused, finished]);
  const restart = () => {
    const next = new PracticeWorld(++seed.current);
    setWorld(next); setView(next.getView()); setDirection(0); setPaused(false);
  };
  const last = view.steps.at(-1);
  const progress = Math.min(100, Math.round(Number(last?.state.y ?? 0) / Math.max(1, view.run?.courseLength ?? 1) * 100));
  return <section className={`practice-game${compact ? ' practice-compact' : ''}`} aria-label="Practice track" data-testid="practice-game" data-direction={direction} data-x={String(last?.state.x ?? 0)}>
    <header className="practice-heading"><div><span className="practice-label">PRACTICE</span><h1>{finished ? 'Finish line. Go again?' : 'Your turn to steer.'}</h1><p>No wallet. No trades. No prizes.</p></div><div className="practice-heading-actions"><SkinPicker /><button className="practice-pause" onClick={() => setPaused(!paused)} aria-label={paused ? 'Resume practice' : 'Pause practice'}>{paused ? <IconPlay /> : <IconPause />}</button></div></header>
    <div className="practice-course">
      <WorldCanvas key={world.getView().run?.seed} view={view} className="practice-canvas" ambient label="Practice course. A or left arrow steers left. D or right arrow steers right. S coasts." pausedLabel="Practice paused" onAim={(x) => steer(x > Number(last?.state.x ?? 0) ? 1 : -1)} />
      <span className="practice-progress">{progress}% of course</span>
      {finished && <div className="practice-result"><strong>Practice complete</strong><span>Try another line through the course.</span><button className="btn primary" onClick={restart}>Run again</button></div>}
    </div>
    <div className="practice-controls" role="group" aria-label="Practice steering">
      <button disabled={paused || finished} aria-pressed={direction === -1} onClick={() => steer(-1)}><IconArrowLeft /><span>Steer left<small>A / ←</small></span></button>
      <button className="practice-coast" disabled={paused || finished} aria-pressed={direction === 0} onClick={() => steer(0)}>Coast<small>S / ↓</small></button>
      <button disabled={paused || finished} aria-pressed={direction === 1} onClick={() => steer(1)}><span>Steer right<small>D / →</small></span><IconArrowRight /></button>
    </div>
    <footer className="practice-footer"><span aria-live="polite">{finished ? 'Practice has no effect on live results.' : direction === 0 ? 'Tap a side or the track to steer.' : `Steering ${direction < 0 ? 'left' : 'right'}. Tap Coast to release.`}</span><button onClick={restart}>Restart</button></footer>
    {children}
  </section>;
}
